import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import {
  GripVertical,
  Eye,
  EyeOff,
  Lock,
  Search,
  X,
  AlertCircle,
  Check,
  Settings2,
  RotateCcw,
  Save,
  Trash2,
  Loader2,
  ChevronsUpDown,
  Users,
  User,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { getDataMode } from '@/lib/data-service';
import {
  fetchColumnPresets,
  createColumnPreset,
  deleteColumnPreset,
  type ColumnPresetDto,
} from '@/lib/column-presets-api';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { Switch } from '@/components/ui/switch';

// Column category type
export type ColumnCategory = 'Financial' | 'Resource' | 'Status' | 'Timeline' | 'Client' | 'Performance';

// Column definition interface
export interface ColumnDefinition {
  id: string;
  name: string;
  label?: string; // Display label (defaults to name if not provided)
  description: string;
  category: ColumnCategory;
  visible: boolean;
  locked?: boolean;
  order: number;
  align?: 'left' | 'right'; // Text alignment for the column
}

// Preset configuration interface
export interface PresetConfiguration {
  id: string;
  name: string;
  columns: Array<{ id: string; visible: boolean }>;
}

// Default column definitions
const DEFAULT_COLUMNS: ColumnDefinition[] = [
  {
    id: 'project-code',
    name: 'Project Code',
    description: 'Unique code identifier for the project',
    category: 'Status',
    visible: true,
    order: 0,
  },
  {
    id: 'project-name',
    name: 'Project Name',
    description: 'Name of the project',
    category: 'Status',
    visible: true,
    locked: true,
    order: 1,
  },
  {
    id: 'project-manager',
    name: 'Project Manager',
    description: 'Assigned project manager',
    category: 'Resource',
    visible: true,
    order: 2,
  },
  {
    id: 'project-status',
    name: 'Project Status',
    description: 'Current project status',
    category: 'Status',
    visible: true,
    order: 3,
  },
  {
    id: 'project-type',
    name: 'Project Type',
    description: 'Type of project (Fixed Price, T&M, Retainer)',
    category: 'Status',
    visible: true,
    order: 4,
  },
  {
    id: 'health-indicator',
    name: 'Health Indicator',
    description: 'Project health status (RAG)',
    category: 'Status',
    visible: true,
    order: 5,
  },
  {
    id: 'client-code',
    name: 'Client Code',
    description: 'Client code identifier',
    category: 'Client',
    visible: true,
    order: 6,
  },
  {
    id: 'client-name',
    name: 'Client Name',
    description: 'Name of the client organisation',
    category: 'Client',
    visible: true,
    order: 7,
  },
  // Financial — Sales Budget group
  {
    id: 'budget',
    name: 'Sales Budget',
    description: 'Total sales budget (from Sales Budget tab totals)',
    category: 'Financial',
    visible: false,
    order: 8,
    align: 'right',
  },
  {
    id: 'actual-sales',
    name: 'Actual Sales',
    description: 'Actual revenue recognised to date (from Sales Budget tab totals)',
    category: 'Financial',
    visible: false,
    order: 9,
    align: 'right',
  },
  {
    id: 'sales-variance',
    name: 'Sales Variance',
    description: 'Remaining sales budget — Sales Budget minus Actual Sales',
    category: 'Financial',
    visible: false,
    order: 10,
    align: 'right',
  },
  // Financial — Cost Budget group
  {
    id: 'cost-budget',
    name: 'Cost Budget',
    description: 'Total cost budget — labour plus expenses (from Cost Budget tab totals)',
    category: 'Financial',
    visible: false,
    order: 11,
    align: 'right',
  },
  {
    id: 'actual-cost',
    name: 'Actual Costs',
    description: 'Total actual costs incurred to date — labour plus expenses (from Cost Budget tab totals)',
    category: 'Financial',
    visible: false,
    order: 12,
    align: 'right',
  },
  {
    id: 'cost-variance',
    name: 'Cost Variance',
    description: 'Remaining cost budget — Cost Budget minus Actual Costs',
    category: 'Financial',
    visible: false,
    order: 13,
    align: 'right',
  },

  {
    id: 'gross-margin',
    name: 'Gross Margin',
    description: 'Gross Margin % — (Actual Sales − Actual Costs) ÷ Actual Sales × 100. Actual Sales = invoices raised. Actual Costs = all cost types including labour, expenses, subcontractors and purchases.',
    category: 'Financial',
    visible: false,
    order: 15,
    align: 'right',
  },
  {
    id: 'billing-status',
    name: 'Billing Status',
    description: 'Current billing and invoice status',
    category: 'Financial',
    visible: false,
    order: 16,
  },
  {
    id: 'wip-amount',
    name: 'WIP Amount',
    description: 'Work in Progress amount. T&M: unbilled time & expenses. Fixed Fee: earned revenue (% complete × contract value) minus amount invoiced to date.',
    category: 'Financial',
    visible: false,
    order: 17,
    align: 'right',
  },
  {
    id: 'gross-profit',
    name: 'Gross Profit',
    description: 'Gross Profit £ — Actual Sales minus Actual Costs. Actual Sales = invoices raised. Actual Costs = all cost types including labour, expenses, subcontractors and purchases.',
    category: 'Financial',
    visible: false,
    order: 18,
    align: 'right',
  },
  {
    id: 'forecast-at-completion',
    name: 'Forecast at Completion',
    description: 'Projected total cost at project completion',
    category: 'Financial',
    visible: false,
    order: 19,
    align: 'right',
  },
  // Resource — Resource Budget group
  {
    id: 'team-members',
    name: 'Team Members',
    description: 'Number of team members assigned',
    category: 'Resource',
    visible: false,
    order: 20,
    align: 'right',
  },
  {
    id: 'allocated-hours',
    name: 'Resource Budget (hrs)',
    description: 'Total budgeted hours for this project (from Resource Budget tab totals)',
    category: 'Resource',
    visible: false,
    order: 21,
    align: 'right',
  },
  {
    id: 'actual-hours',
    name: 'Actual Hours',
    description: 'Total actual hours logged to date (from Resource Budget tab totals)',
    category: 'Resource',
    visible: false,
    order: 22,
    align: 'right',
  },
  {
    id: 'hours-variance',
    name: 'Hours Variance',
    description: 'Remaining resource budget — Resource Budget hours minus Actual Hours',
    category: 'Resource',
    visible: false,
    order: 23,
    align: 'right',
  },
  {
    id: 'utilisation-rate',
    name: 'Utilisation Rate',
    description: 'Resource utilisation percentage',
    category: 'Resource',
    visible: false,
    order: 24,
    align: 'right',
  },
  // Status
  {
    id: 'phase',
    name: 'Phase/Stage',
    description: 'Current project phase or stage',
    category: 'Status',
    visible: false,
    order: 20,
  },
  {
    id: 'completion-percentage',
    name: 'Completion Percentage',
    description: 'Percentage of project completed',
    category: 'Status',
    visible: false,
    order: 21,
    align: 'right',
  },
  {
    id: 'last-updated',
    name: 'Last Updated',
    description: 'When the project was last modified',
    category: 'Status',
    visible: false,
    order: 22,
  },
  // Timeline
  {
    id: 'start-date',
    name: 'Start Date',
    description: 'Project start date',
    category: 'Timeline',
    visible: false,
    order: 23,
  },
  {
    id: 'end-date',
    name: 'End Date',
    description: 'Project end date',
    category: 'Timeline',
    visible: false,
    order: 24,
  },
  {
    id: 'duration',
    name: 'Duration',
    description: 'Total project duration',
    category: 'Timeline',
    visible: false,
    order: 25,
  },
  {
    id: 'days-remaining',
    name: 'Days Remaining',
    description: 'Days until project completion',
    category: 'Timeline',
    visible: false,
    order: 26,
    align: 'right',
  },
  {
    id: 'next-milestone',
    name: 'Next Milestone',
    description: 'Upcoming milestone name and due date',
    category: 'Timeline',
    visible: false,
    order: 27,
  },
  // Client
  {
    id: 'client-contact',
    name: 'Client Contact',
    description: 'Primary client contact person',
    category: 'Client',
    visible: false,
    order: 28,
  },
  {
    id: 'account-manager',
    name: 'Account Manager',
    description: 'Assigned account manager',
    category: 'Client',
    visible: false,
    order: 29,
  },
  // Performance
  {
    id: 'milestones-completed',
    name: 'Milestones Completed',
    description: 'Number of completed milestones',
    category: 'Performance',
    visible: false,
    order: 31,
  },
  {
    id: 'overdue-milestones',
    name: 'Overdue Milestones',
    description: 'Number of milestones past their due date',
    category: 'Performance',
    visible: false,
    order: 32,
  },
  {
    id: 'tasks-overdue',
    name: 'Overdue Tasks',
    description: 'Number of overdue tasks',
    category: 'Performance',
    visible: false,
    order: 33,
  },
  {
    id: 'risk-level',
    name: 'Risk Level',
    description: 'Overall project risk assessment',
    category: 'Performance',
    visible: false,
    order: 34,
  },
  {
    id: 'quality-score',
    name: 'Quality Score',
    description: 'Project quality rating',
    category: 'Performance',
    visible: false,
    order: 35,
  },
];

// Preset configurations
const PRESETS: PresetConfiguration[] = [
  {
    id: 'default',
    name: 'Default View',
    columns: [
      { id: 'project-code', visible: true },
      { id: 'project-name', visible: true },
      { id: 'project-manager', visible: true },
      { id: 'project-status', visible: true },
      { id: 'project-type', visible: true },
      { id: 'health-indicator', visible: true },
      { id: 'client-code', visible: true },
      { id: 'client-name', visible: true },
    ],
  },
  {
    id: 'project-manager',
    name: 'Project Manager View',
    columns: [
      { id: 'project-code', visible: true },
      { id: 'project-name', visible: true },
      { id: 'client-name', visible: true },
      { id: 'project-status', visible: true },
      { id: 'health-indicator', visible: true },
      { id: 'completion-percentage', visible: true },
      { id: 'team-members', visible: true },
      { id: 'tasks-overdue', visible: true },
      { id: 'start-date', visible: true },
      { id: 'end-date', visible: true },
      { id: 'days-remaining', visible: true },
    ],
  },
  {
    id: 'finance',
    name: 'Finance View',
    columns: [
      { id: 'project-code', visible: true },
      { id: 'project-name', visible: true },
      { id: 'client-name', visible: true },
      { id: 'budget', visible: true },
      { id: 'actual-sales', visible: true },
      { id: 'sales-variance', visible: true },
      { id: 'cost-budget', visible: true },
      { id: 'actual-cost', visible: true },
      { id: 'cost-variance', visible: true },
      { id: 'allocated-hours', visible: true },
      { id: 'actual-hours', visible: true },
      { id: 'hours-variance', visible: true },
      { id: 'gross-margin', visible: true },
      { id: 'billing-status', visible: true },
      { id: 'project-status', visible: true },
    ],
  },
  {
    id: 'executive',
    name: 'Executive Summary',
    columns: [
      { id: 'project-code', visible: true },
      { id: 'project-name', visible: true },
      { id: 'client-name', visible: true },
      { id: 'project-manager', visible: true },
      { id: 'health-indicator', visible: true },
      { id: 'budget', visible: true },
      { id: 'gross-margin', visible: true },
      { id: 'completion-percentage', visible: true },
      { id: 'risk-level', visible: true },
    ],
  },
  {
    id: 'resource',
    name: 'Resource View',
    columns: [
      { id: 'project-code', visible: true },
      { id: 'project-name', visible: true },
      { id: 'project-manager', visible: true },
      { id: 'team-members', visible: true },
      { id: 'allocated-hours', visible: true },
      { id: 'actual-hours', visible: true },
      { id: 'hours-variance', visible: true },
      { id: 'utilisation-rate', visible: true },
      { id: 'start-date', visible: true },
      { id: 'end-date', visible: true },
      { id: 'project-status', visible: true },
    ],
  },
];

interface ColumnConfiguratorDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  columns: ColumnDefinition[];
  onSave: (columns: ColumnDefinition[]) => void;
  presets?: PresetConfiguration[]; // Optional custom presets (system/hardcoded)
  defaultColumns?: ColumnDefinition[]; // Optional default columns for reset
  pageKey?: string; // Page identifier for loading/saving presets (e.g. 'projects-list', 'project-transactions')
}

export function ColumnConfiguratorDrawer({
  open,
  onOpenChange,
  columns: initialColumns,
  onSave,
  presets = PRESETS,
  defaultColumns = DEFAULT_COLUMNS,
  pageKey,
}: ColumnConfiguratorDrawerProps) {
  const [columns, setColumns] = useState<ColumnDefinition[]>(initialColumns);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<ColumnCategory | 'all'>('all');
  const [selectedPreset, setSelectedPreset] = useState<string>('custom');
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showSavePresetDialog, setShowSavePresetDialog] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [presetIsShared, setPresetIsShared] = useState(false);
  const [savedPresets, setSavedPresets] = useState<ColumnPresetDto[]>([]);
  const [isLoadingPresets, setIsLoadingPresets] = useState(false);
  const [isSavingPreset, setIsSavingPreset] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<number | null>(null);
  const [presetSearchOpen, setPresetSearchOpen] = useState(false);
  const [presetSearchTerm, setPresetSearchTerm] = useState('');
  const [duplicateError, setDuplicateError] = useState<string | null>(null);

  // Load saved presets from API when drawer opens
  const loadPresets = useCallback(async () => {
    if (!pageKey || getDataMode() !== 'live') return;
    setIsLoadingPresets(true);
    try {
      const presetData = await fetchColumnPresets(pageKey);
      setSavedPresets(presetData);
    } catch (error) {
      console.error('Failed to load column presets:', error);
    } finally {
      setIsLoadingPresets(false);
    }
  }, [pageKey]);

  // Reset state when drawer opens
  useEffect(() => {
    if (open) {
      setColumns(initialColumns);
      setSearchTerm('');
      setCategoryFilter('all');
      setSelectedPreset('custom');
      setHasChanges(false);
      setShowDeleteConfirm(null);
      loadPresets();
    }
  }, [open, initialColumns, loadPresets]);

  // Track changes
  useEffect(() => {
    const changed = JSON.stringify(columns) !== JSON.stringify(initialColumns);
    setHasChanges(changed);
    if (changed && selectedPreset !== 'custom') {
      setSelectedPreset('custom');
    }
  }, [columns, initialColumns, selectedPreset]);

  // Filter columns based on search and category
  const filteredColumns = useMemo(() => {
    return columns.filter((col) => {
      const matchesSearch =
        searchTerm === '' ||
        col.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        col.description.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesCategory = categoryFilter === 'all' || col.category === categoryFilter;

      return matchesSearch && matchesCategory;
    });
  }, [columns, searchTerm, categoryFilter]);

  // Count visible columns
  const visibleCount = columns.filter((col) => col.visible).length;
  const totalCount = columns.length;
  const allVisible = columns.every((col) => col.visible);

  // Handle preset selection
  const handlePresetChange = (presetId: string) => {
    if (presetId === 'custom') {
      setSelectedPreset('custom');
      return;
    }

    // Check system presets first
    const systemPreset = presets.find((p) => p.id === presetId);
    if (systemPreset) {
      const updatedColumns = columns.map((col) => {
        const presetCol = systemPreset.columns.find((pc) => pc.id === col.id);
        return {
          ...col,
          visible: presetCol ? presetCol.visible : false,
        };
      });
      setColumns(updatedColumns);
      setSelectedPreset(presetId);
      return;
    }

    // Check saved presets
    const savedPreset = savedPresets.find((p) => String(p.id) === presetId);
    if (savedPreset) {
      try {
        const presetColumns: Array<{ id: string; visible: boolean }> = JSON.parse(savedPreset.columns);
        const updatedColumns = columns.map((col) => {
          const presetCol = presetColumns.find((pc) => pc.id === col.id);
          return {
            ...col,
            visible: presetCol ? presetCol.visible : false,
          };
        });
        setColumns(updatedColumns);
        setSelectedPreset(presetId);
      } catch (error) {
        console.error('Failed to parse preset columns:', error);
        toast.error('Failed to apply preset');
      }
    }
  };

  // Handle visibility toggle
  const handleToggleVisibility = (columnId: string) => {
    setColumns((prev) =>
      prev.map((col) =>
        col.id === columnId && !col.locked ? { ...col, visible: !col.visible } : col
      )
    );
  };

  // Handle show all
  const handleShowAll = () => {
    setColumns((prev) => prev.map((col) => ({ ...col, visible: true })));
  };

  // Handle hide all
  const handleHideAll = () => {
    setColumns((prev) =>
      prev.map((col) => ({
        ...col,
        visible: col.locked ? true : false,
      }))
    );
  };

  // Handle reset to default
  const handleResetToDefault = () => {
    setColumns(defaultColumns);
    setSelectedPreset('custom');
  };

  // Derived: split saved presets into personal and shared groups
  const personalPresets = useMemo(
    () => savedPresets.filter((p) => !p.isShared),
    [savedPresets]
  );
  const sharedPresets = useMemo(
    () => savedPresets.filter((p) => p.isShared),
    [savedPresets]
  );

  // Check for duplicate preset name within the same group (shared/personal) and pageKey
  const checkDuplicateName = useCallback(
    (name: string, isShared: boolean): boolean => {
      const trimmedName = name.trim().toLowerCase();
      if (!trimmedName) return false;
      const group = isShared ? sharedPresets : personalPresets;
      return group.some((p) => p.name.trim().toLowerCase() === trimmedName);
    },
    [personalPresets, sharedPresets]
  );

  // Handle save preset
  const handleSavePreset = async () => {
    if (!presetName.trim()) return;

    // Duplicate check
    if (checkDuplicateName(presetName, presetIsShared)) {
      const groupLabel = presetIsShared ? 'Shared' : 'Personal';
      setDuplicateError(
        `A ${groupLabel.toLowerCase()} preset named "${presetName.trim()}" already exists. Please choose a different name.`
      );
      return;
    }

    // If we have a pageKey and we're in live mode, save to API
    if (pageKey && getDataMode() === 'live') {
      setIsSavingPreset(true);
      try {
        const columnsJson = JSON.stringify(
          columns.map((col) => ({ id: col.id, visible: col.visible }))
        );
        await createColumnPreset({
          name: presetName.trim(),
          pageKey,
          columns: columnsJson,
          isShared: presetIsShared,
        });
        toast.success(`Preset "${presetName.trim()}" saved`, {
          description: presetIsShared ? 'Shared with everyone in your organisation.' : 'Saved as a personal preset.',
          icon: <Check className="h-4 w-4" />,
        });
        setPresetName('');
        setPresetIsShared(false);
        setDuplicateError(null);
        setShowSavePresetDialog(false);
        // Reload presets to reflect the new one
        await loadPresets();
      } catch (error) {
        console.error('Failed to save preset:', error);
        toast.error('Failed to save preset. Please try again.');
      } finally {
        setIsSavingPreset(false);
      }
    } else {
      // Fallback: save to localStorage
      const presetId = `preset-${Date.now()}`;
      const newPreset: PresetConfiguration = {
        id: presetId,
        name: presetName.trim(),
        columns: columns.map((col) => ({ id: col.id, visible: col.visible })),
      };
      const storedPresets = localStorage.getItem('project-column-presets');
      const existing: PresetConfiguration[] = storedPresets ? JSON.parse(storedPresets) : [];
      existing.push(newPreset);
      localStorage.setItem('project-column-presets', JSON.stringify(existing));
      toast.success(`Preset "${newPreset.name}" saved`, {
        icon: <Check className="h-4 w-4" />,
      });
      setPresetName('');
      setPresetIsShared(false);
      setDuplicateError(null);
      setShowSavePresetDialog(false);
    }
  };

  // Handle delete preset
  const handleDeletePreset = async (presetId: number) => {
    try {
      await deleteColumnPreset(presetId);
      toast.success('Preset deleted', {
        icon: <Check className="h-4 w-4" />,
      });
      setShowDeleteConfirm(null);
      // If the deleted preset was selected, reset to custom
      if (selectedPreset === String(presetId)) {
        setSelectedPreset('custom');
      }
      // Reload presets
      await loadPresets();
    } catch (error) {
      console.error('Failed to delete preset:', error);
      toast.error('Failed to delete preset. Please try again.');
    }
  };

  // Handle drag start
  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  // Handle drag over
  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  // Handle drop
  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const draggedColumn = filteredColumns[draggedIndex];
    const targetColumn = filteredColumns[dropIndex];

    // Reorder in the full columns array
    const newColumns = [...columns];
    const draggedOriginalIndex = newColumns.findIndex((c) => c.id === draggedColumn.id);
    const targetOriginalIndex = newColumns.findIndex((c) => c.id === targetColumn.id);

    newColumns.splice(draggedOriginalIndex, 1);
    newColumns.splice(targetOriginalIndex, 0, draggedColumn);

    // Update order property
    const reorderedColumns = newColumns.map((col, idx) => ({ ...col, order: idx }));

    setColumns(reorderedColumns);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  // Handle drag end
  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  // Handle save
  const handleSave = async () => {
    // Validate at least one column is visible (besides locked ones)
    const visibleNonLockedColumns = columns.filter((col) => col.visible && !col.locked);
    if (visibleNonLockedColumns.length === 0) {
      toast.error('At least one column must be visible in addition to Project Name', {
        description: 'Please select at least one additional column to display alongside Project Name.',
        icon: <AlertCircle className="h-4 w-4" />,
      });
      return;
    }

    setIsSaving(true);
    try {
      // Simulate save delay
      await new Promise((resolve) => setTimeout(resolve, 500));
      onSave(columns);
      toast.success('Column configuration saved', {
        description: 'Your column preferences have been updated.',
        icon: <Check className="h-4 w-4" />,
      });
      onOpenChange(false);
    } catch {
      toast.error('Failed to save configuration', {
        description: 'Please try again or contact support if the problem persists.',
        action: {
          label: 'Retry',
          onClick: () => handleSave(),
        },
      });
    } finally {
      setIsSaving(false);
    }
  };


  return (
    <>
      <Sheet open={open} onOpenChange={(value) => {
          if (showSavePresetDialog) return;
          onOpenChange(value);
        }}>
        <SheetContent 
          className="w-[540px] sm:w-[540px] sm:max-w-none flex flex-col p-0"
          onInteractOutside={(e) => {
            if (showSavePresetDialog) {
              e.preventDefault();
              return;
            }
            // Prevent toast interactions from closing the sheet
            const target = e.target as HTMLElement;
            if (target?.closest?.('[data-sonner-toast]')) {
              e.preventDefault();
            }
          }}
          onEscapeKeyDown={(e) => {
            if (showSavePresetDialog) {
              e.preventDefault();
            }
          }}
        >
          {/* Header */}
          <SheetHeader className="px-6 pt-4 pb-3 flex-shrink-0 border-b gap-1">
            <SheetTitle className="flex items-center gap-2">
              <Settings2 className="h-5 w-5 text-primary" />
              Configure Columns
            </SheetTitle>
          </SheetHeader>

          {/* Content */}
          <div className="flex-1 overflow-hidden flex flex-col">
            {/* Preset Selector - only shown when user has saved presets */}
            {savedPresets.length > 0 && (
            <div className="px-6 pt-4 pb-3 border-b bg-muted/30">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium whitespace-nowrap">
                  Preset:
                </label>
                <Popover open={presetSearchOpen} onOpenChange={setPresetSearchOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={presetSearchOpen}
                      className="w-full justify-between font-normal"
                    >
                      <span className="truncate">
                        {selectedPreset === 'custom'
                          ? 'Custom'
                          : savedPresets.find((p) => String(p.id) === selectedPreset)?.name ?? 'Custom'}
                      </span>
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                    <Command shouldFilter={false}>
                      <CommandInput
                        placeholder="Search presets..."
                        value={presetSearchTerm}
                        onValueChange={setPresetSearchTerm}
                      />
                      <CommandList>
                        <CommandEmpty>No presets found.</CommandEmpty>
                        {/* Custom option */}
                        {(!presetSearchTerm || 'custom'.includes(presetSearchTerm.toLowerCase())) && (
                          <CommandItem
                            value="custom"
                            onSelect={() => {
                              handlePresetChange('custom');
                              setPresetSearchOpen(false);
                              setPresetSearchTerm('');
                            }}
                          >
                            <Check className={cn('mr-2 h-4 w-4', selectedPreset === 'custom' ? 'opacity-100' : 'opacity-0')} />
                            Custom
                          </CommandItem>
                        )}
                        {/* System Presets - hidden for now while we build out user-managed presets.
                            Keeping the code intact so we can restore them later if needed.
                        {presets.length > 0 && (
                          <CommandGroup heading="System Presets">
                            {presets.filter((p) => !presetSearchTerm || p.name.toLowerCase().includes(presetSearchTerm.toLowerCase())).map((preset) => (
                              <CommandItem
                                key={preset.id}
                                value={preset.id}
                                onSelect={() => {
                                  handlePresetChange(preset.id);
                                  setPresetSearchOpen(false);
                                  setPresetSearchTerm('');
                                }}
                              >
                                <Check className={cn('mr-2 h-4 w-4', selectedPreset === preset.id ? 'opacity-100' : 'opacity-0')} />
                                {preset.name}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        )}
                        */}
                        {/* Shared Presets */}
                        {sharedPresets.filter((p) => !presetSearchTerm || p.name.toLowerCase().includes(presetSearchTerm.toLowerCase())).length > 0 && (
                          <>
                            <CommandSeparator />
                            <CommandGroup heading="Shared">
                              {sharedPresets
                                .filter((p) => !presetSearchTerm || p.name.toLowerCase().includes(presetSearchTerm.toLowerCase()))
                                .map((preset) => (
                                <CommandItem
                                  key={`shared-${preset.id}`}
                                  value={`shared-${preset.id}`}
                                  onSelect={() => {
                                    handlePresetChange(String(preset.id));
                                    setPresetSearchOpen(false);
                                    setPresetSearchTerm('');
                                  }}
                                >
                                  <Check className={cn('mr-2 h-4 w-4', selectedPreset === String(preset.id) ? 'opacity-100' : 'opacity-0')} />
                                  <Users className="mr-1 h-3 w-3 text-muted-foreground" />
                                  {preset.name}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </>
                        )}
                        {/* Personal Presets */}
                        {personalPresets.filter((p) => !presetSearchTerm || p.name.toLowerCase().includes(presetSearchTerm.toLowerCase())).length > 0 && (
                          <>
                            <CommandSeparator />
                            <CommandGroup heading="Personal">
                              {personalPresets
                                .filter((p) => !presetSearchTerm || p.name.toLowerCase().includes(presetSearchTerm.toLowerCase()))
                                .map((preset) => (
                                <CommandItem
                                  key={`personal-${preset.id}`}
                                  value={`personal-${preset.id}`}
                                  onSelect={() => {
                                    handlePresetChange(String(preset.id));
                                    setPresetSearchOpen(false);
                                    setPresetSearchTerm('');
                                  }}
                                >
                                  <Check className={cn('mr-2 h-4 w-4', selectedPreset === String(preset.id) ? 'opacity-100' : 'opacity-0')} />
                                  <User className="mr-1 h-3 w-3 text-muted-foreground" />
                                  {preset.name}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </>
                        )}
                        {isLoadingPresets && (
                          <div className="flex items-center gap-2 px-2 py-1.5 text-xs text-muted-foreground">
                            <Loader2 className="h-3 w-3 animate-spin" />
                            Loading presets...
                          </div>
                        )}
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
              {/* Saved Preset Management */}
              {savedPresets.length > 0 && selectedPreset !== 'custom' && savedPresets.some((p) => String(p.id) === selectedPreset) && (
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-xs text-muted-foreground flex-1 truncate">
                    {savedPresets.find((p) => String(p.id) === selectedPreset)?.isShared ? (
                      <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" /> Shared preset</span>
                    ) : (
                      <span className="inline-flex items-center gap-1"><User className="h-3 w-3" /> Personal preset</span>
                    )}
                  </span>
                  {showDeleteConfirm === Number(selectedPreset) ? (
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-destructive">Delete?</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                        onClick={() => handleDeletePreset(Number(selectedPreset))}
                      >
                        <Check className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={() => setShowDeleteConfirm(null)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                      onClick={() => setShowDeleteConfirm(Number(selectedPreset))}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              )}
            </div>
            )}

            {/* Quick Actions + Search */}
            <div className="space-y-2 px-6 pt-4 pb-3">
              {/* Quick Actions */}
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={allVisible ? handleHideAll : handleShowAll}
                  className="gap-2"
                >
                  {allVisible ? (
                    <>
                      <EyeOff className="h-4 w-4" />
                      Hide All
                    </>
                  ) : (
                    <>
                      <Eye className="h-4 w-4" />
                      View All
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleResetToDefault}
                  className="gap-2"
                >
                  <RotateCcw className="h-4 w-4" />
                  Reset Default
                </Button>
              </div>

              {/* Search - full width */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search fields..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 pr-9"
                />
                {searchTerm && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                    onClick={() => setSearchTerm('')}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>

            {/* Column List */}
            <div className="flex-1 overflow-y-auto min-h-0 pt-2 pr-2">
              <div className="space-y-1">
                <div className="flex items-center justify-between px-6">
                  <span className="text-sm font-medium">
                    Fields ({visibleCount} visible)
                  </span>
                  <span className="text-xs text-muted-foreground">
                    Drag to reorder
                  </span>
                </div>

                <div className="px-6 py-2 space-y-1.5">
                  <TooltipProvider>
                  {filteredColumns.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <Search className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p className="text-sm">No columns found</p>
                      <p className="text-xs mt-1">Try adjusting your search or filter</p>
                    </div>
                  ) : (
                    filteredColumns.map((column, index) => (
                      <Tooltip key={column.id}>
                        <TooltipTrigger asChild>
                          <div
                            draggable
                            onDragStart={() => handleDragStart(index)}
                            onDragOver={(e) => handleDragOver(e, index)}
                            onDrop={(e) => handleDrop(e, index)}
                            onDragEnd={handleDragEnd}
                            className={cn(
                              'flex items-center gap-2 p-2 rounded-lg border bg-card transition-all',
                              draggedIndex === index && 'opacity-50 shadow-lg',
                              column.visible ? 'border-primary/20 bg-primary/5' : 'border-border',
                              dragOverIndex === index && 'border-primary border-2'
                            )}
                          >
                            {/* Drag Handle */}
                            <div className="flex-shrink-0">
                              <GripVertical
                                className="h-4 w-4 cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground transition-colors"
                                aria-label="Drag to reorder"
                              />
                            </div>

                            {/* Column Info */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className={cn(
                                  'text-sm font-medium truncate',
                                  !column.visible && 'text-muted-foreground'
                                )}>
                                  {column.name}
                                </span>
                                {column.category && (
                                  <Badge variant="secondary" className="text-xs px-1.5 py-0 h-5">
                                    {column.category}
                                  </Badge>
                                )}
                              </div>
                            </div>

                            {/* Visibility Toggle / Lock indicator */}
                            <div className="flex items-center gap-1">
                              {column.locked ? (
                                <div className="h-8 w-8 flex items-center justify-center" title="Always visible">
                                  <Lock className="h-4 w-4 text-muted-foreground" />
                                </div>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0"
                                  onClick={() => handleToggleVisibility(column.id)}
                                >
                                  {column.visible ? (
                                    <Eye className="h-4 w-4 text-primary" />
                                  ) : (
                                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                                  )}
                                </Button>
                              )}
                            </div>
                          </div>
                        </TooltipTrigger>
                        {column.description && (
                          <TooltipContent side="left">
                            <p>{column.description}</p>
                          </TooltipContent>
                        )}
                      </Tooltip>
                    ))
                  )}
                  </TooltipProvider>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <SheetFooter className="flex-row gap-2 sm:gap-2 flex-shrink-0 mt-0 px-6 py-4 border-t">
            <Button
              variant="outline"
              onClick={() => setShowSavePresetDialog(true)}
              className="flex-1 gap-2"
            >
              <Save className="h-4 w-4" />
              Save Preset
            </Button>
            <Button
              onClick={handleSave}
              disabled={!hasChanges || isSaving}
              className="flex-1"
            >
              {isSaving ? 'Saving...' : 'Apply Changes'}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Save Preset Dialog */}
      <AlertDialog open={showSavePresetDialog} onOpenChange={(open) => {
        setShowSavePresetDialog(open);
        if (!open) {
          setPresetName('');
          setPresetIsShared(false);
          setDuplicateError(null);
        }
      }}>
        <AlertDialogContent className="sm:max-w-xl p-5 gap-3">
          <AlertDialogHeader className="gap-1">
            <AlertDialogTitle>Save Field Configuration</AlertDialogTitle>
            <AlertDialogDescription>
              Give your field configuration a name to save it as a preset for future use.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label htmlFor="preset-name" className="text-sm font-medium">Preset Name</label>
              <Input
                id="preset-name"
                placeholder="e.g., Manager View, Detailed Report"
                value={presetName}
                onChange={(e) => {
                  setPresetName(e.target.value);
                  setDuplicateError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && presetName.trim()) {
                    handleSavePreset();
                  }
                }}
                className={cn('mt-2', duplicateError && 'border-destructive')}
              />
              {duplicateError && (
                <p className="text-xs text-destructive mt-1 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {duplicateError}
                </p>
              )}
            </div>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div className="space-y-0.5">
                <div className="text-sm font-medium flex items-center gap-1.5">
                  {presetIsShared ? (
                    <><Users className="h-4 w-4 text-muted-foreground" /> Shared with Organisation</>
                  ) : (
                    <><User className="h-4 w-4 text-muted-foreground" /> Personal</>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  {presetIsShared
                    ? 'This preset will be visible to everyone in your organisation.'
                    : 'Only you will see this preset.'}
                </p>
              </div>
              <Switch
                checked={presetIsShared}
                onCheckedChange={(checked) => {
                  setPresetIsShared(checked);
                  setDuplicateError(null);
                }}
              />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSavingPreset}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleSavePreset}
              disabled={!presetName.trim() || isSavingPreset}
            >
              {isSavingPreset ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                'Save Preset'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </>
  );
}

// Export default columns for use in other components
export { DEFAULT_COLUMNS, PRESETS };
